import {useNavigate, useParams} from "react-router-dom";
import {Button, Box, Divider, IconButton, Stack, Typography} from "@mui/material";
import {useEffect, useState} from "react";
import {ColDef, ValueFormatterParams} from "ag-grid-community";
import {format} from "date-fns";
import {urls} from "@routes";
import {ArrowBack} from "@mui/icons-material";
import PaginatedServerSideDataTable from "@components/PaginatedServerSideDataTable";
import PageLoading from "@components/PageLoading";
import {ApiRequestFailed, authenticateAndFetchData, retryFn} from "@lib/apis";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import {useQuery} from "@tanstack/react-query";
import { Downloading } from "@mui/icons-material";
import { exportToCSV, CSVColumn } from "@components/ExportToCSV";
import {useSnackbar} from "notistack";
import { getExportFilename } from "@lib/utils";
import {useAuth} from "./AuthProvider";

interface CreditLog {
	id: number
	new_credits: number
	old_credits: number
	workspace_id: string
	workspace_name: string
	reason: string
	date: number
}

interface PaginationData {
	data: CreditLog[]
	total_count: number
	total_pages: number
	current_page: number
	page_size: number
	user_email: string
}

export default function AdminUserCreditLogs() {
	const {userId} = useParams();
	const { user } = useAuth();
	const navigate = useNavigate();
	const {enqueueSnackbar} = useSnackbar();

	const [pageData, setPageData] = useState<PaginationData>();
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(50);
	const [search, setSearch] = useState("");
	const [exportLoading, setExportLoading] = useState(false);

	// Query to fetch page data.
	const pageDataQuery = useQuery({
		queryKey: [
			"adminUserCreditLogsPageData",
			userId,
			page,
			pageSize,
			search
		],
		queryFn: () =>
			authenticateAndFetchData(
				`/admin/user-credit-logs/?user_id=${userId}&page=${page}&page_size=${pageSize}&search=${search}`
			),
		gcTime: 0,
		retry: retryFn,
		refetchOnWindowFocus: false,
	});

	useEffect(() => {
		if (pageDataQuery.data?.data) {
			const response = pageDataQuery.data.data;

			setPageData({
				data: response.data || [],
				total_count: response.total_count,
				total_pages: response.total_pages,
				current_page: response.current_page,
				page_size: response.page_size,
				user_email: response.user_email,
			});
		}
	}, [pageDataQuery.data]);

	const handlePageChange = (newPage: number) => {
		setPage(newPage);
	};

	const handlePageSizeChange = (newPageSize: number) => {
		setPageSize(newPageSize);
		setPage(1);
	};

	const handleSearchChange = (value: string) => {
		setSearch(value);
		setPage(1);
	};

	// Table functions
	function timestampFormatter(params: ValueFormatterParams) {
		if (params.value !== null) {
			return format(new Date(params.value), "do MMM y, HH:mm:ss (xxx)");
		} else {
			return "---"
		}
	}

	const columnCustomizerLabel = {
		date: "Date",
		new_credits: "New Credits",
		old_credits: "Previous Remaining Credits",
		workspace_name: "Workspace Name",
		workspace_id: "Workspace ID",
		reason: "Reason for Change",	
	};

	// Table column definition.
	const columnDefs: ColDef[] = [
		{
			field: "date",
			headerName: "Date",
			valueFormatter: timestampFormatter,
			flex: 2,
		},
		{field: "new_credits", headerName: "New Credits"},
		{field: "old_credits", headerName: "Previous Remaining Credits"},
		{field: "workspace_name", headerName: "Workspace Name"},
		{field: "workspace_id", headerName: "Workspace ID"},
		{field: "reason", headerName: "Reason for Change", flex: 2},
	]

	const creditLogColumns: CSVColumn<any>[] = [    
		{ header: "Workspace ID", accessor: "workspace_id" },
		{ header: "Workspace Name", accessor: "workspace_name" },
		{ header: "Old Credits", accessor: "old_credits" },
		{ header: "New Credits", accessor: "new_credits" },
		{ 
			header: "Credit Difference", 
			accessor: (row) => (
				row.new_credits !== undefined && row.old_credits !== undefined 
					? row.new_credits - row.old_credits 
					: "N/A"
			) 
		},
		{ header: "Reason", accessor: (row) => row.reason || "N/A" },
		{ 
			header: "Date", 
			accessor: (row) => (row.date ? new Date(row.date).toLocaleString() : "N/A") 
		},
	];

	const ExportUserCreditLogsCsvButton = () => {		

		const handleExportCreditLogs = async () => {
			setExportLoading(true);

			try {
				const response = await authenticateAndFetchData(
					`/admin/user-credit-logs/?user_id=${userId}&export_all=true`
				);
				
				const logsToExport = response?.data?.data || [];

				if (!logsToExport.length) {
					enqueueSnackbar("No credit change log data available to export.", { variant: "error" });
					return;
				}

				const success = exportToCSV(
					logsToExport,
					creditLogColumns,
					getExportFilename(user?.email ?? "unknown", "CreditChangeLogs")
				);

				if (success) {
					enqueueSnackbar("Credit change logs exported successfully!", { variant: "success" });
				} else {
					enqueueSnackbar("Failed to generate CSV file.", { variant: "error" });
				}
			} catch (err) {
				console.error("Export error:", err);
				enqueueSnackbar("Error while exporting credit change logs.", { variant: "error" });
			} finally {
				setExportLoading(false);
			}
		};

		return (
			<Button
				startIcon={<Downloading />}
				variant="contained"
				color="primary"
				onClick={handleExportCreditLogs}
				disabled={exportLoading}
			>
				{exportLoading ? "Exporting..." : "Export"}
			</Button>
		);
	};

	// ========================================================================
	// --------------------------- MAIN RENDER CODE ---------------------------
	// ========================================================================

	if (!pageData && pageDataQuery.isLoading) {
		return (
			<PageLoading/>
		)

	} else if (pageDataQuery.error as unknown as ApiRequestFailed) {
		return <PageDataErrorHandler error={pageDataQuery.error as unknown as ApiRequestFailed}/>

	} else if (pageData) {
		return (
			<Stack direction={"column"} spacing={4} sx={{pb: 4, height: "100%"}}>
				<Stack direction={"row"} spacing={2} alignItems={"center"}>
					<IconButton
						onClick={() => navigate(urls["adminUserDetails"].replace(":userId", userId || ""))}><ArrowBack/></IconButton>
					<Typography fontWeight={"bold"} variant={"h5"}>Credit Changes ({pageData.user_email})</Typography>
				</Stack>
				<Divider/>
				<Typography sx={{mb: 4}}>NOTE: Currently this is only for Stripe renewals.</Typography>
				<Box sx={{ height: "100%", maxHeight: 800, width: "100%", overflow: "hidden" }}>
					<PaginatedServerSideDataTable
						columns={columnDefs}
						paginationData={pageData}
						noRowsText={"No Credit History Available"}
						loading={pageDataQuery.isFetching}
						onPageChange={handlePageChange}
						onPageSizeChange={handlePageSizeChange}
						onSearchChange={handleSearchChange}
						searchPlaceholder="Search by reason or workspace..."
						columnLabels={columnCustomizerLabel}
						actions={<ExportUserCreditLogsCsvButton />}
					/>
				</Box>
			</Stack>
		)

	} else {
		// Ideally it should not reach here.
		return <></>
	}
}
